import {
  Users, Briefcase, GitBranch, UserCheck, Clock, TrendingDown, Smile,
  Activity, AlertCircle, MessageSquare, Calendar, Target, ListChecks, AlertTriangle,
} from "lucide-react";
import { Link } from "react-router-dom";
import { registerWidget } from "@/modules/shared/dashboard/registry";
import { KpiWidget } from "@/modules/shared/dashboard/widgets/shells/KpiWidget";
import { ListWidget } from "@/modules/shared/dashboard/widgets/shells/ListWidget";
import { HeadcountTrendChart } from "./shells/HeadcountTrendChart";
import { FunnelByStageChart } from "./shells/FunnelByStageChart";
import { fmtPct, fmtDays } from "@/modules/shared/dashboard/formatters";
import { PIPELINE_STAGES } from "@/modules/talent/hooks/useATSPipeline";

// ── KPI registrations ────────────────────────────────────────────────────
registerWidget({
  id: "talent.headcount", module: "talent", title: "Pessoas ativas",
  description: "Total de profissionais ativos na organização.", size: "1x1", category: "kpi", icon: Users,
  Component: () => (
    <KpiWidget tone="ink" title="Pessoas ativas" endpoint="/talent/dashboard/headcount"
               icon={Users} href="/profissionais" />
  ),
});

registerWidget({
  id: "talent.openJobs", module: "talent", title: "Vagas abertas",
  description: "Total de vagas com status aberto.", size: "1x1", category: "kpi", icon: Briefcase,
  Component: () => (
    <KpiWidget tone="ink" title="Vagas abertas" endpoint="/talent/dashboard/open-jobs"
               icon={Briefcase} href="/vagas" />
  ),
});

registerWidget({
  id: "talent.pipelineCandidates", module: "talent", title: "Candidatos no funil",
  description: "Candidatos ativos em alguma etapa do recrutamento.", size: "1x1", category: "kpi", icon: GitBranch,
  Component: () => (
    <KpiWidget tone="blue" title="Candidatos no funil" endpoint="/talent/dashboard/pipeline"
               icon={GitBranch} href="/recrutamento" />
  ),
});

registerWidget({
  id: "talent.hiresThisMonth", module: "talent", title: "Contratações no mês",
  description: "Profissionais contratados no mês corrente.", size: "1x1", category: "kpi", icon: UserCheck,
  Component: () => (
    <KpiWidget tone="green" title="Contratações no mês" endpoint="/talent/dashboard/hires"
               icon={UserCheck} href="/profissionais" />
  ),
});

registerWidget({
  id: "talent.avgDaysToHire", module: "talent", title: "Dias até contratar",
  description: "Tempo médio entre abertura da vaga e contratação (últimos 90 dias).", size: "1x1", category: "kpi", icon: Clock,
  Component: () => (
    <KpiWidget tone="amber" title="Dias até contratar" endpoint="/talent/dashboard/avg-days-to-hire"
               icon={Clock} href="/recrutamento" valueFormatter={fmtDays} />
  ),
});

registerWidget({
  id: "talent.turnoverQtr", module: "talent", title: "Rotatividade (trim.)",
  description: "Percentual de saídas no trimestre corrente.", size: "1x1", category: "kpi", icon: TrendingDown,
  Component: () => (
    <KpiWidget tone="red" title="Rotatividade (trim.)" endpoint="/talent/dashboard/turnover"
               icon={TrendingDown} href="/profissionais" valueFormatter={fmtPct} />
  ),
});

registerWidget({
  id: "talent.enps", module: "talent", title: "eNPS atual",
  description: "Resultado da última pesquisa de cultura.", size: "1x1", category: "kpi", icon: Smile,
  Component: () => (
    <KpiWidget tone="green" title="eNPS atual" endpoint="/talent/dashboard/enps"
               icon={Smile} href="/cultura" />
  ),
});

registerWidget({
  id: "talent.utilization", module: "talent", title: "Ocupação",
  description: "Percentual de profissionais ativos com alocação vigente.", size: "1x1", category: "kpi", icon: Activity,
  Component: () => (
    <KpiWidget tone="blue" title="Ocupação" endpoint="/talent/dashboard/utilization"
               icon={Activity} href="/profissionais" valueFormatter={fmtPct} />
  ),
});

// ── List registrations ───────────────────────────────────────────────────
type StuckCandidate = { id: string; name: string; stage: string; days_stuck: number };
registerWidget({
  id: "talent.stuckCandidates", module: "talent", title: "Candidatos parados",
  description: "Candidatos sem mudança de etapa há mais de 14 dias.", size: "2x1", category: "list", icon: AlertCircle,
  Component: () => (
    <ListWidget<StuckCandidate>
      title="Candidatos parados +14d"
      endpoint="/talent/dashboard/stuck-candidates"
      icon={AlertCircle}
      seeAllHref="/recrutamento"
      renderItem={(c) => (
        <Link to={`/recrutamento/${c.id}`} className="block hover:text-[var(--ink)]">
          <span className="flex items-center justify-between text-sm">
            <span>{c.name} <span className="text-muted-foreground">· {PIPELINE_STAGES.find(s => s.key === c.stage)?.label ?? c.stage}</span></span>
            <span className="text-warning">{c.days_stuck}d</span>
          </span>
        </Link>
      )}
    />
  ),
});

type OverdueCheckin = { id: string; name: string; days_overdue: number };
registerWidget({
  id: "talent.overdueCheckins", module: "talent", title: "Check-ins atrasados",
  description: "Profissionais com check-in periódico vencido.", size: "2x1", category: "list", icon: MessageSquare,
  Component: () => (
    <ListWidget<OverdueCheckin>
      title="Check-ins atrasados"
      endpoint="/talent/dashboard/overdue-checkins"
      icon={MessageSquare}
      seeAllHref="/checkins"
      renderItem={(it) => (
        <Link to={`/profissionais/${it.id}`} className="block hover:text-[var(--ink)]">
          <span className="flex items-center justify-between text-sm">
            <span>{it.name}</span>
            <span className="text-danger">{it.days_overdue}d</span>
          </span>
        </Link>
      )}
    />
  ),
});

type StaleJob = { id: string; title: string; days_idle: number };
registerWidget({
  id: "talent.staleJobs", module: "talent", title: "Vagas sem movimentação",
  description: "Vagas sem nenhuma atividade há mais de 30 dias.", size: "2x1", category: "list", icon: Calendar,
  Component: () => (
    <ListWidget<StaleJob>
      title="Vagas sem movimentação +30d"
      endpoint="/talent/dashboard/stale-jobs"
      icon={Calendar}
      seeAllHref="/vagas"
      renderItem={(j) => (
        <Link to={`/vagas/${j.id}/pipeline`} className="block hover:text-[var(--ink)]">
          <span className="flex items-center justify-between text-sm">
            <span>{j.title}</span>
            <span className="text-warning">{j.days_idle}d</span>
          </span>
        </Link>
      )}
    />
  ),
});

type OverduePdi = { id: string; owner_name: string; title: string; due_date: string };
registerWidget({
  id: "talent.overduePdis", module: "talent", title: "PDIs vencidos",
  description: "Itens de plano de desenvolvimento com prazo expirado.", size: "2x1", category: "list", icon: Target,
  Component: () => (
    <ListWidget<OverduePdi>
      title="PDIs vencidos"
      endpoint="/talent/dashboard/overdue-pdis"
      icon={Target}
      seeAllHref="/pdi"
      renderItem={(it) => (
        <Link to="/pdi" className="block hover:text-[var(--ink)]">
          <span className="text-sm">
            <strong>{it.owner_name}</strong> — {it.title}
          </span>
        </Link>
      )}
    />
  ),
});

type OverdueAP = { id: string; title: string; owner_name: string; due_date: string };
registerWidget({
  id: "talent.overdueActionPlans", module: "talent", title: "Planos de ação atrasados",
  description: "Planos de ação com prazo vencido e ainda não concluídos.", size: "2x1", category: "list", icon: ListChecks,
  Component: () => (
    <ListWidget<OverdueAP>
      title="Planos de ação atrasados"
      endpoint="/talent/dashboard/overdue-action-plans"
      icon={ListChecks}
      seeAllHref="/planos-de-acao"
      renderItem={(it) => (
        <Link to="/planos-de-acao" className="block hover:text-[var(--ink)]">
          <span className="text-sm">
            <strong>{it.owner_name}</strong> — {it.title}
          </span>
        </Link>
      )}
    />
  ),
});

// ── Cross-module KPI registrations ──────────────────────────────────────
registerWidget({
  id: "talent.revenueAtRisk",
  module: "talent",
  title: "Receita em risco",
  description: "% do MRR alocado em clientes marcados como risco.",
  size: "1x1",
  category: "kpi",
  icon: AlertTriangle,
  Component: () => (
    <KpiWidget
      tone="red"
      title="Receita em risco"
      endpoint="/talent/dashboard/revenue-at-risk"
      icon={AlertTriangle}
      href="/clientes"
      valueFormatter={(n) => `${n.toFixed(1)}%`}
    />
  ),
});

// ── Chart registrations ──────────────────────────────────────────────────
registerWidget({
  id: "talent.headcountTrend6m", module: "talent", title: "Pessoas (6 meses)",
  description: "Evolução do headcount ativo nos últimos 6 meses.",
  size: "2x1", category: "chart", icon: Users,
  Component: HeadcountTrendChart,
});

registerWidget({
  id: "talent.funnelByStage", module: "talent", title: "Funil por etapa",
  description: "Distribuição de candidatos por etapa do processo seletivo.",
  size: "2x1", category: "chart", icon: GitBranch,
  Component: FunnelByStageChart,
});
